import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Monitor, 
  X,
  ChevronUp,
  Maximize,
  Minimize
} from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, StepResource } from '../../types';
import {
  NumberLineComponent,
  TenFrameComponent,
  CountObjectsComponent,
  MatchingObjectsComponent,
  InteractiveDiagramComponent,
  SimulationComponent,
  FlashcardSetComponent,
  GridActivityComponent,
  AudioBankComponent,
  TimeSequenceComponent,
  MeasurementComponent,
  CurrencyComponent,
  TimerComponent,
  QuizComponent,
  SortingCanvasComponent,
  SpatialCanvasComponent,
  TopologyCanvasComponent,
  PatternMakerComponent,
  ShapeRecognitionComponent,
  SizeComparisonComponent,
  AdditionManipulatorComponent,
  EvaluationDashboardComponent,
  ProjectWorkspaceComponent,
  MathToolboxComponent,
  CollaborationBoardComponent,
  DrawingBoardComponent,
  ObjectManipulatorComponent,
  MemoryGameComponent,
  SortingGameComponent,
  ImageComponent,
  RichTextComponent
} from '../ResourceComponents';
import { useTrackingContext } from '../../contexts/TrackingContext';

interface ResourcesPanelProps {
  resources: Record<string, Resource>;
  currentStep: any;
  onResourceAction?: (resourceId: string, action: string, data?: any) => void;
  isVisible?: boolean;
}

export const ResourcesPanel: React.FC<ResourcesPanelProps> = ({ 
  resources, 
  currentStep, 
  onResourceAction,
  isVisible = false
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { trackResourceInteraction } = useTrackingContext();
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop } = scrollContainerRef.current;
      setShowScrollTop(scrollTop > 100);
    }
  };

  const handleClose = () => {
    onResourceAction?.('panel', 'close', 'mobile_close');
  };

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const renderResourceComponent = (resourceId: string, mergedResource?: Resource) => {
    const resource = mergedResource || resources[resourceId];
    if (!resource) return null;

    switch (resource.type) {
      case 'number_line':
        return (
          <NumberLineComponent
            resource={resource}
            component={{ type: 'display', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'ten_frame':
        return (
          <TenFrameComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'count_objects_canvas':
        return (
          <CountObjectsComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'matching_objects':
        return (
          <MatchingObjectsComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'interactive_diagram':
        return (
          <InteractiveDiagramComponent
            resource={resource}
            component={{ type: 'display', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'simulation':
        return (
          <SimulationComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'flashcard_set':
        return (
          <FlashcardSetComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'grid_activity':
        return (
          <GridActivityComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'audio_bank':
        return (
          <AudioBankComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'time_sequence':
        return (
          <TimeSequenceComponent
            resource={resource}
          />
        );
      
      case 'measurement':
        return (
          <MeasurementComponent
            resource={resource}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'currency':
        return (
          <CurrencyComponent
            resource={resource}
            onAction={(action, data) => {
              trackResourceInteraction(resource.id, action, data?.duration || 0, data?.status || false, data?.score || 0)
              .catch(error => console.error('Error tracking resource interaction:', error));
            }}
          />
        );
      
      case 'timer':
        return (
          <TimerComponent
            seconds={resource.seconds || 60}
            onComplete={() => onResourceAction?.(resourceId, 'completed')}
            autoLaunch={resource.auto_launch || false}
            resetKey={currentStep?.id || 'default'}
          />
        );
      
      case 'quiz':
        return (
          <QuizComponent
            resource={resource}
            component={{ type: 'assessment', component_id: resourceId, props: {} }}
            onComplete={(results) => onResourceAction?.(resourceId, 'completed', results)}
          />
        );
      
      case 'image':
        return (
          <ImageComponent
            resource={resource}
            component={{ type: 'display', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'rich_text':
        return (
          <RichTextComponent
            resource={resource}
            component={{ type: 'display', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      // GPF Resource Types
      case 'sorting_canvas':
        return (
          <SortingCanvasComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'spatial_canvas':
        return (
          <SpatialCanvasComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'topology_canvas':
        return (
          <TopologyCanvasComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'pattern_maker':
        return (
          <PatternMakerComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'shape_recognition':
        return (
          <ShapeRecognitionComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'size_comparison':
        return (
          <SizeComparisonComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'addition_manipulator':
        return (
          <AdditionManipulatorComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'evaluation_dashboard':
        return (
          <EvaluationDashboardComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'project_workspace':
        return (
          <ProjectWorkspaceComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'math_toolbox':
        return (
          <MathToolboxComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'collaboration_board':
        return (
          <CollaborationBoardComponent
            resource={resource as any}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'drawing_board':
        return (
          <DrawingBoardComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'object_manipulator':
        return (
          <ObjectManipulatorComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'memory_game':
        return (
          <MemoryGameComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      case 'sorting_game':
        return (
          <SortingGameComponent
            resource={resource}
            component={{ type: 'manipulative', component_id: resourceId, props: {} }}
            onAction={(action, data) => onResourceAction?.(resourceId, action, data)}
          />
        );
      
      default:
        return (
          <div className="p-4 text-center text-gray-500">
            {t('resourcesPanel.unsupportedResourceType')}: {resource.type}
          </div>
        );
    }
  };

  // Normalize step resources to a consistent structure and handle legacy shapes
  const stepResources: StepResource[] = Array.isArray(currentStep?.resources)
    ? (currentStep.resources as any[])
        .map((entry) => {
          if (!entry) return null;
          // If entry is a string, treat it as a resourceId
          if (typeof entry === 'string') {
            return { resourceId: entry } as StepResource;
          }
          // If entry is an object, prefer resourceId, fallback to id or nested config.resourceId (legacy)
          const resolvedId = entry.resourceId || entry.id || entry?.config?.resourceId;
          if (!resolvedId) return null;
          return { ...entry, resourceId: resolvedId } as StepResource;
        })
        .filter((r): r is StepResource => !!r)
    : [];

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ 
        x: isVisible ? 0 : '100%'
      }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className={`fixed top-16 pb-20 right-0 h-full bg-white shadow-2xl z-50 flex flex-col ${
        isMinimized ? 'w-16' : 'w-full md:w-96 lg:w-96 xl:w-96'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        {!isMinimized ? (
          <>
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <Monitor className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {t('resourcesPanel.resources')}
                </h2>
                <p className="text-sm text-gray-600 truncate">
                  {stepResources.length} {t('resourcesPanel.resourcesForStep')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title={isCollapsed ? t('resourcesPanel.expand') : t('resourcesPanel.collapse')}
              >
                <ChevronUp className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={() => setIsMinimized(true)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('resourcesPanel.minimize')}
              >
                <Minimize className="w-4 h-4" />
              </button>
              <button
                onClick={handleClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('resourcesPanel.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center w-full space-y-2">
            <Monitor className="w-5 h-5 text-blue-600" />
            <button
              onClick={() => setIsMinimized(false)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('resourcesPanel.maximize')}
            >
              <Maximize className="w-4 h-4" />
            </button>
            <div className="text-xs text-gray-500 text-center px-1">
              {stepResources.length}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {!isMinimized && !isCollapsed && (
        <div className="flex-1 overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="h-full overflow-y-auto"
            onScroll={handleScroll}
          >
            {stepResources.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Monitor className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">
                  {t('resourcesPanel.noResources')}
                </p>
                <p className="text-sm">
                  {t('resourcesPanel.noResourcesDescription')}
                </p>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {stepResources.map((stepResource: StepResource, index: number) => {
                  const resolvedResourceId = stepResource.resourceId || (stepResource as any)?.id || (stepResource as any)?.config?.resourceId;
                  const resource = resources[resolvedResourceId];
                  if (!resource) {
                    console.warn(`Resource not found: ${resolvedResourceId}`);
                    return null;
                  }

                  const mergedResource = stepResource.config
                    ? { ...resource, ...stepResource.config }
                    : resource;

                  return (
                    <div key={resolvedResourceId || `resource-${index}`} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-700 truncate flex-1 mr-2">
                          {resource?.type || 'Unknown Resource'}
                        </h3>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                          {resolvedResourceId}
                        </span>
                        {stepResource.config && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex-shrink-0">
                            {t('resourcesPanel.customConfig')}
                          </span>
                        )}
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        {renderResourceComponent(resolvedResourceId, mergedResource)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Scroll to Top Button */}
          {showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={scrollToTop}
              className="absolute bottom-4 right-4 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
              title={t('resourcesPanel.scrollToTop')}
            >
              <ChevronUp className="w-4 h-4" />
            </motion.button>
          )}
        </div>
      )}
    </motion.div>
  );
};
